import Btn from '@/components/Button/Button.vue';
import PlacesSidebar from '@/views/Brand/Places/Components/Sidebar.vue';
import FilterToggler from '@/components/Filters/FilterToggler.vue';
import { fontNormal } from '@/util/formatters';
import { computed, defineComponent, onMounted, ref, } from '@vue/composition-api';
import { useBrand, useMobileView, useRouteQuery } from '@/composables';
import { useAuthUser } from '@/composables/use-auth-user';
import { UserRole, SystemPrivilege, SystemRole } from '@/api/requests/users';
import { useViewer } from '@/composables/use-viewer';
import { createNamespacedHelpers } from 'vuex-composition-helpers';
import { PlacesTable } from '@/views/Brand/Places/_components/PlacesTable';
import { PortalTarget } from '@/constants/portal';
import ContentWrapper from '@/layouts/BrandLayout/Components/ContentWrapper.vue';
import { mdiTrayArrowDown } from '@mdi/js';
import { usePlaceResourcesStore } from '@/store/place-resources';
import { SelectionMode } from '@/store/_composables/use-resources';
import { mapId } from '@/util/common';
import { ClusteredMap } from '@/components/Map';
import { MapDataType } from '@/components/Map/types';
import { Budgets } from '@/views/Brand/Places/_components/budgets';
import SlotTabs from '@/components/Tabs/SlotTabs.vue';
import { PlacesTabName } from '@/views/Brand/Places/_constants';
export default defineComponent({
    name: 'Places',
    components: {
        SlotTabs,
        Budgets,
        ClusteredMap,
        PlacesTable,
        Btn,
        PlacesSidebar,
        FilterToggler,
        ContentWrapper,
    },
    setup(_props, ctx) {
        const { isMobileView } = useMobileView();
        const { routeQuery, selectLimit } = useRouteQuery(ctx);
        const { brand } = useBrand();
        const { isEmployee, hasRoleAccessOrHigher, isObjektoAdmin, hasPrivilege } = useAuthUser();
        const { isPreviewMode, viewer } = useViewer(ctx);
        const { useActions } = createNamespacedHelpers('user');
        const { refreshUserProfile } = useActions(['refreshUserProfile']);
        const selectedPlaces = ref([]);
        const isProfileLoaded = ref(isPreviewMode.value);
        const checkPrivilege = computed(() => {
            if (isPreviewMode.value && viewer.value && viewer.value.privileges) {
                return viewer.value.privileges.includes(SystemPrivilege.BUDGET_ACCESS);
            }
            return hasPrivilege.value(SystemPrivilege.BUDGET_ACCESS);
        });
        const checkIsEmployee = computed(() => {
            if (isPreviewMode.value && viewer.value) {
                return viewer.value.systemRole === SystemRole.EMPLOYEE;
            }
            return isEmployee.value;
        });
        const showBudgets = computed(() => brand.value &&
            brand.value.isBudgetEnabled &&
            !isMobileView.value &&
            (checkPrivilege.value || !checkIsEmployee.value));
        const activeTab = ref(PlacesTabName.LIST);
        onMounted(async () => {
            if (!isPreviewMode.value) {
                await refreshUserProfile();
                isProfileLoaded.value = true;
                if (showBudgets.value) {
                    activeTab.value = PlacesTabName.BUDGETS;
                }
            }
            else {
                if (showBudgets.value) {
                    activeTab.value = PlacesTabName.BUDGETS;
                }
            }
        });
        const structureAdminOrHigherAccess = computed(() => hasRoleAccessOrHigher.value(UserRole.STRUCTURE_ADMIN));
        const formatLabel = (label) => `#${label}`;
        const mapQuery = computed(() => {
            const result = { ...routeQuery.value };
            if (result && brand.value) {
                delete result.page;
                delete result.limit;
            }
            return {
                ...result,
                brandId: brand.value.id,
            };
        });
        const createReport = () => {
            const { onPlaceSelection, setPlaceSelectionMode } = usePlaceResourcesStore();
            setPlaceSelectionMode(SelectionMode.INSTANT);
            onPlaceSelection(mapId(selectedPlaces.value), selectedPlaces.value);
            setPlaceSelectionMode(SelectionMode.DELAYED);
            ctx.root.$router.push({
                name: 'Reports',
                params: {
                    addReport: true,
                },
            });
        };
        const tabs = computed(() => {
            return {
                ...(showBudgets.value && {
                    [PlacesTabName.BUDGETS]: {
                        name: PlacesTabName.BUDGETS,
                        label: ctx.root.$t('brand-places-list-view.budgets'),
                    },
                }),
                [PlacesTabName.LIST]: {
                    name: PlacesTabName.LIST,
                    label: ctx.root.$t('brand-places-list-view.list'),
                },
                [PlacesTabName.MAP]: {
                    name: PlacesTabName.MAP,
                    label: ctx.root.$t('brand-places-list-view.map'),
                },
            };
        });
        return {
            mdiTrayArrowDown,
            structureAdminOrHigherAccess,
            isEmployee,
            checkIsEmployee,
            isObjektoAdmin,
            fontNormal,
            tabs,
            mapQuery,
            formatLabel,
            brand,
            selectedPlaces,
            routeQuery,
            selectLimit,
            PortalTarget,
            createReport,
            MapDataType,
            isMobileView,
            activeTab,
            PlacesTabName,
            isProfileLoaded,
            showBudgets,
        };
    },
});
